import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// Screensaver — Matrix-style falling green characters
// Only activates when manually triggered (via Display Properties or context menu)
// No longer auto-starts on idle

const COLUMNS = 40;
const CHARS = 'ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍ0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function MatrixScreensaver({ active: externalActive, onDismiss }) {
    const [internalActive, setInternalActive] = useState(false);
    const canvasRef = useRef(null);
    const animRef = useRef(null);
    const dropsRef = useRef([]);

    const active = externalActive !== undefined ? externalActive : internalActive;

    const dismiss = useCallback(() => {
        if (onDismiss) onDismiss();
        else setInternalActive(false);
        if (animRef.current) cancelAnimationFrame(animRef.current);
    }, [onDismiss]);

    // Matrix animation
    useEffect(() => {
        if (!active || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const fontSize = Math.floor(canvas.width / COLUMNS);
        const columns = Math.floor(canvas.width / fontSize);
        dropsRef.current = Array(columns).fill(1);

        let lastTime = 0;
        const FPS = 20;
        const interval = 1000 / FPS;

        function draw(timestamp) {
            if (timestamp - lastTime < interval) {
                animRef.current = requestAnimationFrame(draw);
                return;
            }
            lastTime = timestamp;

            ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#0F0';
            ctx.font = `${fontSize}px monospace`;

            for (let i = 0; i < dropsRef.current.length; i++) {
                const char = CHARS[Math.floor(Math.random() * CHARS.length)];
                ctx.fillStyle = Math.random() > 0.98 ? '#fff' : '#0F0';
                ctx.fillText(char, i * fontSize, dropsRef.current[i] * fontSize);

                if (dropsRef.current[i] * fontSize > canvas.height && Math.random() > 0.975) {
                    dropsRef.current[i] = 0;
                }
                dropsRef.current[i]++;
            }

            animRef.current = requestAnimationFrame(draw);
        }

        animRef.current = requestAnimationFrame(draw);

        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current);
        };
    }, [active]);

    if (!active) return null;

    return (
        <Container onClick={dismiss} onMouseMove={dismiss} onKeyDown={dismiss}>
            <Canvas ref={canvasRef} />
            <HintText>Move mouse or press any key to return</HintText>
        </Container>
    );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Container = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999998;
  background: #000;
  cursor: none;
  animation: ${fadeIn} 1s ease;
`;

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
`;

const HintText = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: #0a0;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  opacity: 0.4;
`;

export default MatrixScreensaver;
